<template>
  <div class="content-job-list">
    <h2 class="title">{{ data.title }}</h2>
    <div
      v-if="noJobsForCompanyFilter && data.noJobsForCompanyFilterText"
      class="no-jobs-for-company-filter"
      v-html="data.noJobsForCompanyFilterText"
    />
    <div class="positions" :class="{ '-filter-active': isFilterActive }">
      <JobFilter
        :positions="positions"
        :preselected-employment-types="preselectedEmploymentTypes"
        @filter-change="handleFilterChange"
      />
      <span class="indicator-text">{{ indicatorText }}</span>
      <div
        v-for="position in positions"
        :key="position.id"
        class="posting-container"
        :class="{ '-active': isFilterActive && isPositionActive(position) }"
      >
        <JobPosting :position="position" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { XMLParser } from 'fast-xml-parser';
import type { ContentJobListFragment } from '#gql';
import type { JobPosition } from '~/types/jobsList.types';
import JobFilter from '~/components/content-modules/components/JobFilter.vue';
import JobPosting from '~/components/content-modules/components/JobPosting.vue';
import { resolvePreselectedEmploymentTypes } from '~/utils/jobs/employmentTypeFilter';
import {
  buildDepartmentFilterMap,
  resolveDepartmentFilterLabel
} from '~/utils/jobs/departmentFilter';

type ContentJobListData = ContentJobListFragment & {
  preselectedEmploymentTypeOptions?: unknown | null;
};

const props = defineProps<{
  data: ContentJobListData;
}>();

const positions = ref<JobPosition[]>([]);
const activeFilters = ref<Record<string, string[]>>({
  name: [],
  employmentType: [],
  schedule: [],
  location: [],
  country: [],
  department: []
});
const noJobsForCompanyFilter = ref(false);

interface PersonioV2Job {
  id?: string;
  name?: string;
  created_at?: {
    'date-time'?: string;
  };
  _meta?: {
    links?: {
      self?: {
        href?: string;
      };
    };
  };
}

interface PersonioV2JobsResponse {
  _data: PersonioV2Job[];
  _meta?: {
    links?: {
      next?: {
        href?: string;
      };
    };
  };
}

interface PersonioV2OrgUnit {
  id?: string;
  name?: string;
  parent_chain?: string[];
}

interface PersonioV2OrgUnitsResponse {
  _data: PersonioV2OrgUnit[];
  _meta?: {
    links?: {
      next?: {
        href?: string;
      };
    };
  };
}

try {
  const personioV1Url = 'https://nussli.jobs.personio.de/xml?language=cs_CZ';
  const { data } = await useFetch(personioV1Url);

  if (!data.value) {
    throw new Error('Personio API error');
  }

  const parser = new XMLParser();
  const xmlData = parser.parse(data.value as string);
  const raw = xmlData['workzag-jobs']?.position;
  positions.value = Array.isArray(raw) ? raw : raw ? [raw] : [];

  function extractJobIdFromSelfHref(job: PersonioV2Job): string | null {
    const href = job._meta?.links?.self?.href;
    if (!href) {
      return null;
    }
    return href.split('/').pop() ?? null;
  }

  function isNonEmptyString(id: string | null | undefined): id is string {
    return Boolean(id);
  }

  // Prototype hybrid integration:
  // - Keep v1 fields (schedule, office) for current filters/UI.
  // - Enrich overlapping fields from v2 where matching job IDs exist.
  const { data: v2Data, error: v2Error } =
    await useFetch<PersonioV2JobsResponse>('/api/personio/jobs-v2');

  if (v2Error.value) {
    console.error('Failed to fetch Personio v2 job positions:', v2Error.value);
  } else if (v2Data.value?._data?.length) {
    const v2ById = new Map<string, PersonioV2Job>();

    for (const job of v2Data.value._data) {
      const ids = [job.id, extractJobIdFromSelfHref(job)];
      const nonEmptyIds = ids.filter(isNonEmptyString);
      for (const id of nonEmptyIds) {
        v2ById.set(id, job);
      }
    }

    positions.value = positions.value.map((position) => {
      const match = v2ById.get(String(position.id));
      if (!match) {
        return position;
      }

      return {
        ...position,
        name: match.name ?? position.name,
        createdAt: match.created_at?.['date-time'] ?? position.createdAt
      };
    });
  } else {
    console.log('Personio v2 response contains no jobs.');
  }

  // get org units
  try {
    const orgUnits: PersonioV2OrgUnit[] = [];
    let cursor: string | undefined;

    // Fetch all pages to make sure parent departments are available.
    do {
      const { data: orgUnitsData, error: orgUnitsError } =
        await useFetch<PersonioV2OrgUnitsResponse>(
          '/api/personio/org-units-v2',
          {
            query: {
              type: 'department',
              limit: 100,
              sort: 'name',
              include_parent_chain: true,
              cursor
            }
          }
        );

      if (orgUnitsError.value) {
        throw orgUnitsError.value;
      }

      if (!orgUnitsData.value?._data?.length) {
        break;
      }

      orgUnits.push(...orgUnitsData.value._data);

      const nextHref = orgUnitsData.value._meta?.links?.next?.href;
      cursor = nextHref
        ? new URL(nextHref).searchParams.get('cursor') ?? undefined
        : undefined;
    } while (cursor);

    if (orgUnits.length) {
      const orgUnitsById = new Map<string, { id: string; name: string }>();
      const orgUnitsByName = new Map<string, { id: string; name: string }>();
      const parentChainById = new Map<string, string[]>();

      for (const orgUnit of orgUnits) {
        if (!orgUnit.id || !orgUnit.name) {
          continue;
        }
        const unit = { id: String(orgUnit.id), name: orgUnit.name };
        orgUnitsById.set(unit.id, unit);
        orgUnitsByName.set(orgUnit.name.trim().toLowerCase(), unit);
        parentChainById.set(unit.id, orgUnit.parent_chain ?? []);
      }

      positions.value = positions.value.map((position) => {
        const departmentKey = String(position.department).trim().toLowerCase();
        const currentUnit =
          orgUnitsById.get(String(position.department)) ??
          orgUnitsByName.get(departmentKey);
        if (!currentUnit) {
          return position;
        }

        const rawParentChain = parentChainById.get(currentUnit.id);
        const topMostParentId =
          rawParentChain && rawParentChain.length > 0
            ? rawParentChain[rawParentChain.length - 1]
            : currentUnit.id;
        const topMostParent = orgUnitsById.get(String(topMostParentId));
        const topMostParentName = topMostParent?.name;

        if (!topMostParentName) {
          return position;
        }
        return {
          ...position,
          departmentParent: topMostParentName
        };
      });
    }
  } catch (e) {
    console.error('Failed to fetch Personio v2 org units:', e);
  }
} catch (e) {
  console.error('Failed to fetch job positions:', e);
}

// enrich positions with country from DatoCMS job locations
try {
  const { data: jobLocationsData } = await useAsyncGql({
    operation: 'GetAllJobLocations'
  });
  const jobLocations =
    (
      jobLocationsData.value as {
        allJobLocations: Array<{
          locationName?: string | null;
          translatedName?: string | null;
          country?: string | null;
        }>;
      }
    )?.allJobLocations ?? [];

  if (jobLocations.length) {
    const locationByOfficeKey = new Map<
      string,
      { country: string; translatedName?: string }
    >();
    for (const loc of jobLocations) {
      if (!loc.locationName || !loc.country) {
        continue;
      }
      locationByOfficeKey.set(loc.locationName.trim().toLowerCase(), {
        country: loc.country,
        translatedName: loc.translatedName?.trim() || undefined
      });
    }

    positions.value = positions.value.map((position) => {
      const match = locationByOfficeKey.get(
        position.office.trim().toLowerCase()
      );
      if (!match) {
        console.warn(
          '[ContentJobList] No job location match for office:',
          position.office
        );
        return { ...position, locationLabel: position.office };
      }
      return {
        ...position,
        country: match.country,
        locationLabel: match.translatedName ?? position.office
      };
    });
  }
} catch (e) {
  console.error('Failed to fetch job locations from DatoCMS:', e);
}

// enrich positions with department filter labels from DatoCMS job departments
try {
  const { data: jobDepartmentsData } = await useAsyncGql({
    operation: 'GetAllJobDepartments'
  });
  const jobDepartments =
    (
      jobDepartmentsData.value as {
        allJobDepartments: Array<{
          personioDepartmentName?: string | null;
          filterOption?: {
            translatedDisplayName?: string | null;
          } | null;
        }>;
      }
    )?.allJobDepartments ?? [];

  const departmentFilterByPersonioName = buildDepartmentFilterMap(jobDepartments);

  positions.value = positions.value.map((position) => {
    const departmentFilterLabel = resolveDepartmentFilterLabel(
      position.department,
      position.departmentParent,
      departmentFilterByPersonioName
    );

    if (!departmentFilterLabel) {
      console.warn(
        '[ContentJobList] No department filter label for department:',
        position.department
      );
      return position;
    }

    return {
      ...position,
      departmentFilterLabel
    };
  });
} catch (e) {
  console.error('Failed to fetch job departments from DatoCMS:', e);
}

// filter job positions by companyFilter, if companyFilter is not empty
if (props.data.companyFilter) {
  positions.value = positions.value.filter(
    (position) => position.subcompany === props.data.companyFilter
  );
  // If filtered positions are empty,
  // mark that no jobs are available for the selected company.
  if (positions.value.length === 0) {
    console.log(
      'No jobs found for this company, setting noJobsForCompanyFilter to true'
    );
    noJobsForCompanyFilter.value = true;
  }
}
positions.value.sort(
  (a: JobPosition, b: JobPosition) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
);

const employmentTypeOptions = [
  ...new Set(positions.value.map((position) => position.employmentType))
];

const preselectedEmploymentTypes = resolvePreselectedEmploymentTypes(
  props.data.preselectedEmploymentTypeOptions,
  employmentTypeOptions
);

activeFilters.value.employmentType = preselectedEmploymentTypes;

const isFilterActive = computed(() =>
  Object.values(activeFilters.value).some((arr) => arr.length > 0)
);

const filteredPositionsCount = computed(
  () => positions.value.filter((position) => isPositionActive(position)).length
);

const indicatorText = computed(() => {
  if (filteredPositionsCount.value === 0) {
    return 'Keine Stellen gefunden';
  }
  return `${filteredPositionsCount.value} Jobs gefunden`;
});

function isPositionActive(position: JobPosition): boolean {
  const { name, employmentType, schedule, location, country, department } =
    activeFilters.value;
  if (name.length && !name.includes(position.name)) {
    return false;
  }
  if (
    employmentType.length &&
    !employmentType.includes(position.employmentType)
  ) {
    return false;
  }
  if (
    schedule.length &&
    (!position.schedule || !schedule.includes(position.schedule))
  ) {
    return false;
  }
  const locationLabel = position.locationLabel ?? position.office;
  if (location.length && !location.includes(locationLabel)) {
    return false;
  }
  if (
    country.length &&
    (!position.country || !country.includes(position.country))
  ) {
    return false;
  }
  const departmentToFilter = position.departmentFilterLabel;
  if (
    department.length &&
    (!departmentToFilter || !department.includes(departmentToFilter))
  ) {
    return false;
  }
  return true;
}

function handleFilterChange(filters: Record<string, string[]>) {
  activeFilters.value = {
    ...activeFilters.value,
    ...filters
  };
}
</script>

<style scoped lang="scss">
.content-job-list {
  row-gap: 3.5rem;
  position: relative;
  padding: var(--base-component-padding-block)
    var(--base-component-padding-inline);

  > .no-jobs-for-company-filter {
    @include text-sm(300);
    color: var(--base-color-foreground-faded);
    padding-bottom: 2rem;

    > :global(p) {
      max-width: 100%;
    }
  }

  > .title {
    @include text-2xl(700);
    padding-bottom: 5rem;
  }

  @media (--vs) {
    transform: unset !important;

    > .title {
      padding-bottom: 1.5rem;
    }
  }

  > .positions {
    display: grid;
    grid-template-columns: auto;

    &.-filter-active:not(:has(.posting-container.-active)) {
      > .posting-container {
        display: block;
      }
    }

    > .indicator-text {
      @include text-sm(300);
      padding-bottom: 1rem;
      color: var(--base-color-foreground-faded);

      @media (--vs) {
        grid-column: 1 / span 4;
        padding-left: 0;
        padding-top: 1.5rem;
      }
    }

    &:has(.posting-container.-active) {
      > .indicator-text {
        @media (--vs) {
          padding-top: 1.5rem;
        }
      }
      > .posting-container:not(.-active) {
        display: none;
      }
    }

    :deep(.posting-container) {
      grid-column: 1 / span 4;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      column-gap: 2rem;

      @media (--vs) {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
